









































































































































































































import { Component, Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";
import _ from "lodash";

/**
 * Utils
 */
import classes from "@/config/wow/classes";
import * as externalLinks from "@/config/external-links";
import * as imageLinks from "@/config/image-links";

import { SYNC_GUILD_MEMBERS_PERIOD } from "@/config/frequency-times";

import { getTimeSince } from "@/utils/date-helpers";
import { sortByAsc } from "@/utils/sort-helpers";
import { normalizeString } from "@/utils/string-helpers";

/**
 * Components
 */
import CharacterCell from "@/components/_global/CharacterCell.vue";
import CharacterSpecCell from "@/components/_global/CharacterSpecCell.vue";
import AddCharacterDialog from "./components/AddCharacterDialog.vue";

/**
 * API
 */
import teamsAPI from "@/api/teams";
import { GetOneTeamResponse } from "@/api/teams/get-one-team.response";
import DataFetcher from "@/components/_global/DataFetcher.vue";


/**
 * Types
 */

/**
 * VueX Modules
 */
const teamModule = namespace("team");


/**
 * Team characters page.
 */
@Component({
	components: {
		DataFetcher,
		CharacterSpecCell,
		CharacterCell,
		AddCharacterDialog
	}
})
export default class TeamCharactersPage extends Vue {
	$refs!: {
		addCharacterDialog: AddCharacterDialog;
	};

	/// Injections
	classes = classes;
	externalLinks = externalLinks;
	imageLinks = imageLinks;

	getTimeSince = getTimeSince;

	/// Attributes
	isLoading = false;
	teamCharacters: any[] | null = null;

	guildCharacters: any[] | null = null;

	searchField = "";

	lastSyncTime: any | null = null;
	isSynchronizing: boolean | null = null;


	get gaugePercentage() {
		if (this.teamCharacters == null) {
			return null;
		}
		return 100 * this.teamCharacters.length / this.currentTeam.premium.nbMaximumCharactersPerTeam;
	}

	/**
	 * Can the current guild be synchronized (updated / refreshed) ?
	 */
	get canSyncGuild(): boolean {
		return !this.isSynchronizing && !this.isLoading && (this.lastSyncTime == null || (Date.now() - Date.parse(this.lastSyncTime)) > SYNC_GUILD_MEMBERS_PERIOD);
	}

	get displayedCharacters(): any[] | null {
		if (this.teamCharacters == null) {
			return null;
		}

		const normalizedSearchField = normalizeString(this.searchField);
		return _.uniqWith([
			...this.teamCharacters.map((c: any) => ({
				...c,
				guildRank: (c.guildRank == null || !c.isInGuild) ? 100 : c.guildRank
			})),
			...this.guildCharacters != null && this.isInSelectFromGuildMode ? this.guildCharacters.map((c: any) => ({
				name: c.name,
				realm: c.realm,
				realmSlug: c.realmSlug,
				role: c.role,
				guildRank: c.guildRank,
				class: c.class,
				rostersUsedIn: [],
				isInGuild: true
			})) : []
		], (x, y) => x.name == y.name && x.realm == y.realm)
			.filter(c =>
				c.name && normalizeString(c.name)
					.includes(
						normalizedSearchField
					)
			);

	}

	get headers() {
		const headers: any[] = [{
			text: "",
			value: "role",
			align: "left",
			class: "collapsing"
		}];

		if (this.teamCharacters == null || this.teamCharacters.find(x => x.isInGuild) != null) {
			headers.push({
				text: this.$t("character.rank"),
				value: "guildRank",
				align: "left",
				class: "collapsing"
			});
		}

		headers.push({
			text: this.$t("character.name"),
			value: "name",
			align: "left"
		});
		headers.push({
			text: this.$t("character.class"),
			value: "class",
			align: "left"
		});
		headers.push({
			text: this.$t("character.realm"),
			value: "realm",
			align: "left",
			class: "collapsing"
		});

		headers.push({
			text: this.$t("character.guild"),
			value: "guildName",
			align: "left",
			class: "collapsing"
		});


		if (this.teamCharacters?.some(x => x.rostersUsedIn.length > 0)) {
			headers.push({
				text: this.$t("character.rosters"),
				value: "rostersUsedIn",
				align: "left",
				sortable: true,
				class: "collapsing",
				sort: sortByAsc(x => x.filter((r: any) => r.isInRoster).length)
			});
		}


		// headers.push({
		// 	text: this.$t("character.is_approved"),
		// 	value: "isApproved",
		// 	align: "center",
		// 	class: "collapsing"
		// });
		headers.push({
			text: "",
			value: "kick",
			align: "center",
			class: "collapsing",
			sortable: false
		});

		return headers;
	}

	// Pagination
	page = 1;
	pageCount = 0;

	/// VueX Modules
	@teamModule.State("team")
	currentTeam!: GetOneTeamResponse;

	isInSelectFromGuildMode = false;


	/// Methods
	async created() {
		this.$teamHub.$on("team-sync", this.onGuildSync);
	}

	async fetch() {
		await this.fetchTeamCharacters();
		await this.fetchGuildCharacters();
	}

	beforeDestroy() {
		this.$teamHub.$off("team-sync", this.onGuildSync);
	}

	async onGuildSync() {
		await this.fetchGuildCharacters();
	}

	async fetchTeamCharacters() {
		this.teamCharacters = await teamsAPI.getTeamCharacters(this.currentTeam.id);
	}

	async fetchGuildCharacters() {
		const {
			characters,
			time,
			isSynchronizing
		} = await teamsAPI.getGuildCharacters(this.currentTeam.id);
		this.guildCharacters = characters;
		this.lastSyncTime = time;
		this.isSynchronizing = isSynchronizing;
	}

	// async handleToggleApprovedClick(characterID: any) {
	// 	this.isLoading = true;
	// 	try {
	// 		await teamsAPI.toggleApprovedCharacter(this.currentTeam.id, characterID);
	// 		await this.fetchTeamCharacters();
	// 	}
	// 	catch (error) {
	// 		this.handleAPIError(error);
	// 	}
	// 	this.isLoading = false;
	// }

	async handleKickClick(characterID: any) {
		const answer = window.confirm("Do you really want to kick this character ?");
		if (!answer) {
			return;
		}

		this.isLoading = true;
		try {
			await teamsAPI.kickCharacter(this.currentTeam.id, characterID);
			await this.fetchTeamCharacters();
		} catch (error) {
			this.handleAPIError(error);
		}
		this.isLoading = false;
	}

	async handleOpenAddCharacterClick() {
		await this.$refs.addCharacterDialog.open();
	}

	async handleAfterAddCharacter() {
		await this.fetchTeamCharacters();
	}


	async handleAddCharacterToTeamClick(realmSlug: string, characterName: string) {
		this.isLoading = true;
		try {
			await teamsAPI.addCharacter(this.currentTeam.id, realmSlug, characterName);
			await this.fetchTeamCharacters();
			//await this.fetchGuildCharacters();
		} catch (err) {
			this.handleAPIError(err);
		}
		this.isLoading = false;
	}

	async handleToggleSelectCharactersFromGuild() {
		this.isInSelectFromGuildMode = !this.isInSelectFromGuildMode;
	}

	async handleSyncClick() {
		this.isLoading = true;
		try {
			await teamsAPI.syncGuildMembers(this.currentTeam.id);
			await this.fetchGuildCharacters();
		} catch (err) {
			this.handleAPIError(err);
		}
		this.isLoading = false;
	}

}
